import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSwipeable } from 'react-swipeable';
import {
  type DrawerMenuItem,
  getOddsFormatIs,
  type MobileHeaderPageStackItem,
  type NavItemNoIcon,
  ODDS_FORMAT,
  type OddsFormat,
  type SiteConfig,
} from '@cx-web/domain-types';
import classnames from 'classnames';

import Icon from '../../../../../../primitives/icon/index';
import { type HeaderNavItemTrackingData } from '../../../../types';
import StateDropdown from '../../../StateDropdown/index';
import DrawerMenuLinks from '../DrawerMenuLinks/index';

import styles from './Drawer.module.css';

interface MobleHeaderDrawerProps {
  firstLevelNav: NavItemNoIcon[];
  isOpen: boolean;
  mobileHamburgerNav: DrawerMenuItem[];
  oddsFormat: OddsFormat;
  onNavItemClick?: (item: HeaderNavItemTrackingData) => void;
  onOddsFormatChange: (format: OddsFormat) => void;
  onStateChange?: (state: string) => void;
  pages: DrawerMenuItem[];
  path: string;
  selectedState?: string;
  serverUrl: string;
  showOddsFormat: boolean;
  siteConfig: SiteConfig;
  stateOptions?: Array<{ code: string; label: string }>;
  toggleDrawer: () => void;
}

type DrawerOddsFormatProps = { oddsFormat: OddsFormat; onOddsFormatChange: (format: OddsFormat) => void };

function DrawerOddsFormat({ oddsFormat, onOddsFormatChange }: DrawerOddsFormatProps) {
  const oddsFormatIs = getOddsFormatIs(oddsFormat);
  const { t } = useTranslation(['label']);
  const label = t('RESPONSIVE_ODDS_FORMAT');

  const renderOption = (
    format: OddsFormat,
    isSelected: boolean,
    testId: string,
    optionLabel: string,
    example: string,
  ) => (
    <button
      aria-pressed={isSelected}
      className={classnames(styles.format, isSelected && styles.selected)}
      data-testid={testId}
      onClick={() => !isSelected && onOddsFormatChange(format)}
      type="button"
    >
      {optionLabel}
      <span className={styles.example}>{example}</span>
    </button>
  );

  return (
    <>
      <div className={styles.heading}>{label}</div>
      <div aria-label={label} className={styles.formats} data-testid="odds-format" role="group">
        {renderOption(
          ODDS_FORMAT.AMERICAN,
          oddsFormatIs.American,
          'odds-format-option-american',
          t('RESPONSIVE_ODDS_FORMAT_AMERICAN'),
          '+125',
        )}
        {renderOption(
          ODDS_FORMAT.DECIMAL,
          oddsFormatIs.Decimal,
          'odds-format-option-decimal',
          t('RESPONSIVE_ODDS_FORMAT_DECIMAL'),
          '2.25',
        )}
        {renderOption(
          ODDS_FORMAT.FRACTIONAL,
          oddsFormatIs.Fractional,
          'odds-format-option-fractional',
          t('RESPONSIVE_ODDS_FORMAT_FRACTIONAL'),
          '5/4',
        )}
      </div>
    </>
  );
}

function Drawer({
  firstLevelNav,
  isOpen,
  mobileHamburgerNav,
  oddsFormat,
  onNavItemClick,
  onOddsFormatChange,
  onStateChange,
  pages,
  path = '',
  selectedState,
  serverUrl,
  showOddsFormat,
  siteConfig,
  stateOptions,
  toggleDrawer,
}: MobleHeaderDrawerProps) {
  const [pageStack, setPageStack] = useState<MobileHeaderPageStackItem[]>([]);

  useEffect(() => {
    setPageStack([]);
  }, [path]);

  const topOffset = useRef(0);

  // Capture and reset body scroll
  useEffect(() => {
    const body = document.querySelector('body');
    if (!body) return;
    if (isOpen) {
      topOffset.current = body?.getBoundingClientRect()?.top;
    } else {
      window.scrollTo(0, Math.abs(topOffset.current));
    }
  }, [isOpen]);

  const handlers = useSwipeable({
    onSwipedLeft: () => toggleDrawer(),
  });

  const trackMenu = (label: string): void => {
    if (label) onNavItemClick?.({ label });
  };

  const clickBack = () => {
    const [lastPage, ...remainingPages] = pageStack;
    lastPage?.clickEvent?.();
    setPageStack(remainingPages);
  };

  const clickForward = (item: MobileHeaderPageStackItem) => {
    setPageStack([item, ...pageStack]);
  };

  return (
    <div
      className={classnames(styles.navDrawer, isOpen ? styles.open : '')}
      data-testid="nav-drawer"
      {...handlers}
      key={path}
    >
      <div className={styles.drawerHeader}>
        {!!pageStack?.length && (
          <div className={styles.drawerBackButton} onClick={clickBack}>
            <Icon className={styles.drawerBackArrow} data-testid="nav-drawer-back" icon="Arrow" />
            {pageStack[0].label}
          </div>
        )}
        <button className={styles.closeBtn} data-testid="close-button" onClick={toggleDrawer}>
          <Icon className={styles.closeIcon} icon="Close" />
        </button>
      </div>

      <div className={styles.pages}>
        <DrawerMenuLinks
          bookmakers={siteConfig.bookmakers}
          pageClick={clickForward}
          pages={pages}
          serverUrl={serverUrl}
          trackMenu={trackMenu}
        />
        {showOddsFormat && <DrawerOddsFormat oddsFormat={oddsFormat} onOddsFormatChange={onOddsFormatChange} />}
        <StateDropdown
          firstLevelNav={firstLevelNav}
          mobileHamburgerNav={mobileHamburgerNav}
          onStateChange={onStateChange}
          selectedState={selectedState}
          stateOptions={stateOptions}
        />
      </div>
    </div>
  );
}

export default Drawer;
